/* eslint-disable fp/no-mutation */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { ReactElement, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useFormik } from 'formik'
import * as yup from 'yup'
import axios from 'src/utilities/http-client'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchAccreditations,
  selectAccreditationsByCourseID,
  selectAccreditationsStatus,
} from 'src/v3/create/create.slice'
import {
  AddIcon,
  DeleteForeverIcon,
  EditIcon,
  LeftArrowIcon,
  RightArrowIcon,
} from 'src/v3/core/icons'
import { AccreditationType, Course } from 'src/v3/core/types'
import ConfirmAction from 'src/v3/components/confirm-action'
import useSession from 'src/authentication/hooks/use-session'

interface Props {
  course?: Course
  setStep: React.Dispatch<
    React.SetStateAction<
      'basic-info' | 'classes' | 'accreditations' | 'publishing'
    >
  >
}

let accreditationTypes: AccreditationType[] = [
  'notAccredited',
  'capce',
  'ceBroker',
  'newJersey',
  'newYork',
  'virginia',
]

let accreditationLabels: Record<AccreditationType, string> = {
  notAccredited: 'Not Accredited',
  capce: 'CAPCE',
  ceBroker: 'CE Broker',
  newJersey: 'New Jersey',
  newYork: 'New York',
  virginia: 'Virginia',
}

export default ({ course, setStep }: Props): ReactElement => {
  let dispatch = useAppDispatch()
  let navigate = useNavigate()
  let session = useSession()

  let accreditations = useAppSelector(
    selectAccreditationsByCourseID(course?.id || ''),
  )
  let accreditationsStatus = useAppSelector(selectAccreditationsStatus)

  let [errorMessage, setErrorMessage] = useState<string>('')
  let [saving, setSaving] = useState<boolean>(false)
  let [deleting, setDeleting] = useState<string>('')
  let [formOpen, setFormOpen] = useState<boolean>(false)

  let isAdmin = session?.roles.includes('admin')

  let form = useFormik({
    validationSchema: yup.object({
      accreditationType: yup.string().not(['unselected']).required(),
      courseCode: yup.string(),
      topic: yup.string(),
      subTopic: yup.string(),
      topicArea: yup.string(),
      areaNumberALS: yup.string(),
      areaNumberBLS: yup.string(),
      professionCode: yup.string(),
      subjectArea: yup.string(),
      state: yup.string(),
    }),
    enableReinitialize: true,
    initialValues: {
      id: '',
      accreditationType: 'unselected',
      courseCode: '',
      topic: '',
      subTopic: '',
      topicArea: '',
      areaNumberALS: '',
      areaNumberBLS: '',
      professionCode: '',
      subjectArea: '',
      state: '',
    },
    onSubmit: async (values) => {
      if (values.accreditationType === 'unselected') {
        setErrorMessage('Accreditation Type must be selected')
        return
      }

      if (!course) {
        setErrorMessage('Course must be created first')
        return
      }

      setSaving(true)

      try {
        let baseAccreditation: Record<string, any> = {
          courseID: course.id,
          courseCode: values.courseCode.trim(),
        }

        if (values.accreditationType === 'virginia') {
          baseAccreditation = {
            courseID: course.id,
            topic: values.topic.trim(),
            subTopic: values.subTopic.trim(),
            areaNumberALS: values.areaNumberALS.trim(),
            areaNumberBLS: values.areaNumberBLS.trim(),
          }
        } else if (values.accreditationType === 'ceBroker') {
          baseAccreditation = {
            courseID: course.id,
            courseCode: values.courseCode.trim(),
            professionCode: values.professionCode.trim(),
            subjectArea: values.subjectArea.trim(),
            state: values.state,
          }
        } else if (values.accreditationType === 'newYork') {
          baseAccreditation = {
            courseID: course.id,
            topicArea: values.topicArea,
          }
        }

        if (!values.id) {
          await axios.post('/api/v2/create/accreditation', {
            [values.accreditationType]: baseAccreditation,
          })
        } else {
          await axios.put('/api/v2/create/accreditation', {
            [values.accreditationType]: {
              id: values.id,
              ...baseAccreditation,
            },
          })
        }

        await dispatch(fetchAccreditations())
        setFormOpen(false)
        form.resetForm()
      } catch (err: any) {
        setErrorMessage(
          err?.response?.data || err?.message || 'Error saving accreditation',
        )
      } finally {
        setSaving(false)
      }
    },
  })

  useEffect(() => {
    if (accreditationsStatus === 'uninitialized') {
      dispatch(fetchAccreditations())
    }
  }, [accreditationsStatus])

  useEffect(() => {
    if (!formOpen) {
      form.resetForm()
    }
  }, [formOpen])

  let hasAccreditation = (accreditationType: AccreditationType) =>
    accreditations.some(
      (a) =>
        a[accreditationType]?.courseID === course?.id &&
        (!form.values.id || a[accreditationType]?.id !== form.values.id),
    )

  let onEdit = (id: string) => {
    let accreditation = accreditations.find((a) => {
      if (a.capce) return a.capce.id === id
      if (a.newJersey) return a.newJersey.id === id
      if (a.newYork) return a.newYork.id === id
      if (a.virginia) return a.virginia.id === id
      if (a.ceBroker) return a.ceBroker.id === id
      if (a.notAccredited) return a.notAccredited.id === id
      return false
    })

    if (!accreditation) return

    let data: any = {}
    let accType = ''

    if (accreditation.capce) {
      data = accreditation.capce
      accType = 'capce'
    } else if (accreditation.newJersey) {
      data = accreditation.newJersey
      accType = 'newJersey'
    } else if (accreditation.newYork) {
      data = accreditation.newYork
      accType = 'newYork'
    } else if (accreditation.virginia) {
      data = accreditation.virginia
      accType = 'virginia'
    } else if (accreditation.ceBroker) {
      data = accreditation.ceBroker
      accType = 'ceBroker'
    } else if (accreditation.notAccredited) {
      data = accreditation.notAccredited
      accType = 'notAccredited'
    }

    form.setValues({
      id: data.id,
      accreditationType: accType,
      courseCode: data.courseCode || '',
      topic: data.topic || '',
      subTopic: data.subTopic || '',
      topicArea: data.topicArea || '',
      areaNumberALS: data.areaNumberALS || '',
      areaNumberBLS: data.areaNumberBLS || '',
      professionCode: data.professionCode || '',
      subjectArea: data.subjectArea || '',
      state: data.state || '',
    })
    setFormOpen(true)
  }

  let onDelete = async (id: string) => {
    try {
      await axios.delete(
        `/api/v2/create/delete-accreditation?accreditationID=${id}`,
      )
      await dispatch(fetchAccreditations())
    } catch (err: any) {
      setErrorMessage(err?.message || 'Error deleting accreditation')
    }
  }

  let renderAccreditationFields = () => {
    let accType = form.values.accreditationType

    if (accType === 'virginia') {
      return (
        <>
          <TextField
            fullWidth
            className="mt-4"
            variant="standard"
            value={form.values.topic}
            label="Topic"
            name="topic"
            onChange={form.handleChange}
          />
          <TextField
            fullWidth
            className="mt-4"
            variant="standard"
            value={form.values.subTopic}
            label="Sub Topic"
            name="subTopic"
            onChange={form.handleChange}
          />
          <TextField
            fullWidth
            className="mt-4"
            variant="standard"
            value={form.values.areaNumberALS}
            label="Area Number ALS"
            name="areaNumberALS"
            onChange={form.handleChange}
          />
          <TextField
            fullWidth
            className="mt-4"
            variant="standard"
            value={form.values.areaNumberBLS}
            label="Area Number BLS"
            name="areaNumberBLS"
            onChange={form.handleChange}
          />
        </>
      )
    }

    if (accType === 'ceBroker') {
      return (
        <>
          <TextField
            fullWidth
            className="mt-4"
            variant="standard"
            value={form.values.courseCode}
            label="Course Code"
            name="courseCode"
            onChange={form.handleChange}
          />
          <TextField
            fullWidth
            className="mt-4"
            variant="standard"
            value={form.values.professionCode}
            label="Profession Code"
            name="professionCode"
            onChange={form.handleChange}
          />
          <TextField
            fullWidth
            className="mt-4"
            variant="standard"
            value={form.values.subjectArea}
            label="Subject Area"
            name="subjectArea"
            onChange={form.handleChange}
          />
          <FormControl fullWidth className="mt-4">
            <InputLabel>State</InputLabel>
            <Select
              variant="standard"
              value={form.values.state}
              label="State"
              name="state"
              onChange={form.handleChange}
            >
              <MenuItem value="Florida">Florida</MenuItem>
              <MenuItem value="Kentucky">Kentucky</MenuItem>
              <MenuItem value="Tennessee">Tennessee</MenuItem>
            </Select>
          </FormControl>
        </>
      )
    }

    if (accType === 'newYork') {
      return (
        <FormControl fullWidth className="mt-4">
          <InputLabel>Topic Area</InputLabel>
          <Select
            variant="standard"
            value={form.values.topicArea}
            label="Topic Area"
            name="topicArea"
            onChange={form.handleChange}
          >
            <MenuItem value="Cardiovascular Emergencies">
              Cardiovascular Emergencies
            </MenuItem>
            <MenuItem value="Trauma">Trauma</MenuItem>
            <MenuItem value="Medical">Medical</MenuItem>
            <MenuItem value="Pediatrics">Pediatrics</MenuItem>
            <MenuItem value="Operations">Operations</MenuItem>
          </Select>
        </FormControl>
      )
    }

    // Default: courseCode field for capce, notAccredited, etc.
    return (
      <TextField
        fullWidth
        className="mt-4"
        variant="standard"
        value={form.values.courseCode}
        label="Course Code"
        name="courseCode"
        onChange={form.handleChange}
      />
    )
  }

  return (
    <Box>
      <Typography variant="h3" sx={{ mb: 3 }}>
        Course Accreditations
      </Typography>

      {!course && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Please complete the Basic Info step first to manage accreditations.
        </Alert>
      )}

      {course && (
        <>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Add accreditations for this course. At least one accreditation is
            required to publish the course. If your content is not accredited,
            select "Not Accredited" for the accreditation type, the course code
            you enter will be shown on certificates to assist in your own
            tracking.
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<AddIcon />}
              onClick={() => setFormOpen(true)}
              disabled={!course}
            >
              Add Accreditation
            </Button>
          </Box>

          <Grid container spacing={2}>
            {accreditations
              .filter(
                (a) =>
                  a.capce?.courseID === course.id ||
                  a.ceBroker?.courseID === course.id ||
                  a.newJersey?.courseID === course.id ||
                  a.newYork?.courseID === course.id ||
                  a.virginia?.courseID === course.id ||
                  a.notAccredited?.courseID === course.id,
              )
              .map((a, i) => {
                let accredData: any = null
                let accredType = ''
                let accredId = ''

                if (a.capce) {
                  accredData = a.capce
                  accredType = 'capce'
                  accredId = a.capce.id
                } else if (a.ceBroker) {
                  accredData = a.ceBroker
                  accredType = 'ceBroker'
                  accredId = a.ceBroker.id
                } else if (a.newJersey) {
                  accredData = a.newJersey
                  accredType = 'newJersey'
                  accredId = a.newJersey.id
                } else if (a.newYork) {
                  accredData = a.newYork
                  accredType = 'newYork'
                  accredId = a.newYork.id
                } else if (a.virginia) {
                  accredData = a.virginia
                  accredType = 'virginia'
                  accredId = a.virginia.id
                } else if (a.notAccredited) {
                  accredData = a.notAccredited
                  accredType = 'notAccredited'
                  accredId = a.notAccredited.id
                }

                return (
                  <Grid key={`${accredType}-${i}`} item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            mb: 2,
                          }}
                        >
                          <Chip
                            label={
                              accreditationLabels[
                                accredType as AccreditationType
                              ]
                            }
                            color="primary"
                            variant="outlined"
                          />
                          <Box>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => onEdit(accredId)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() =>
                                  setDeleting(`delete-${accredId}`)
                                }
                              >
                                <DeleteForeverIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>

                        <Typography variant="body2" color="text.secondary">
                          {accredData?.courseCode &&
                            `Code: ${accredData.courseCode}`}
                          {accredData?.topic && `Topic: ${accredData.topic}`}
                          {accredData?.topicArea &&
                            `Area: ${accredData.topicArea}`}
                          {accredData?.state && `State: ${accredData.state}`}
                        </Typography>
                      </CardContent>
                    </Card>

                    <ConfirmAction
                      executeAction={() => onDelete(accredId)}
                      open={deleting}
                      setOpen={setDeleting}
                      openText={`delete-${accredId}`}
                      title="Confirm Deletion"
                      text={`Are you sure you want to delete this ${accreditationLabels[accredType as AccreditationType]} accreditation?`}
                      onFailure={(err) =>
                        setErrorMessage(
                          err.message || 'Error deleting accreditation',
                        )
                      }
                    />
                  </Grid>
                )
              })}
          </Grid>
        </>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" onClick={() => navigate('/create/v4')}>
            Save and Exit
          </Button>
          <Button
            variant="outlined"
            startIcon={<LeftArrowIcon />}
            onClick={() => setStep('classes')}
          >
            Back to Classes
          </Button>
        </Box>

        <Button
          variant="contained"
          endIcon={<RightArrowIcon />}
          onClick={() => setStep('publishing')}
        >
          Continue to Publishing
        </Button>
      </Box>

      {/* Add/Edit Accreditation Dialog */}
      <Dialog
        open={formOpen && !saving}
        onClose={() => setFormOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {form.values.id ? 'Edit' : 'Add'} Accreditation
        </DialogTitle>
        <DialogContent>
          <form onSubmit={form.handleSubmit}>
            <FormControl fullWidth sx={{ mt: 1 }}>
              <InputLabel>Accreditation Type</InputLabel>
              <Select
                variant="standard"
                value={form.values.accreditationType}
                label="Accreditation Type"
                name="accreditationType"
                onChange={form.handleChange}
                error={
                  form.touched.accreditationType &&
                  !!form.errors.accreditationType
                }
              >
                <MenuItem value="unselected">Select Type</MenuItem>
                {accreditationTypes
                  .filter((type) => isAdmin || type === 'notAccredited')
                  .filter((type) => !hasAccreditation(type))
                  .map((type) => (
                    <MenuItem key={type} value={type}>
                      {accreditationLabels[type]}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            {form.values.accreditationType !== 'unselected' &&
              renderAccreditationFields()}

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2,
                mt: 3,
              }}
            >
              <Button variant="outlined" onClick={() => setFormOpen(false)}>
                Cancel
              </Button>
              <Button variant="contained" type="submit" disabled={saving}>
                {saving ? 'Saving...' : form.values.id ? 'Update' : 'Add'}
              </Button>
            </Box>
          </form>
        </DialogContent>
      </Dialog>

      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  )
}
